/**
 * AgendaPagosComisiones — Planeación de caja para desembolsos de comisión.
 *
 * ¿Qué responde esta pantalla?
 *   "¿Cuánto voy a girar a las agencias, en qué fecha, y a cuál?"
 *
 * El módulo "Comisiones por pagar" muestra el total agregado por agencia.
 * Acá lo distribuimos en el calendario de salidas para planear caja día a
 * día: cuánto sale el 17-ago, el 18-ago, etc. — y a qué agencia.
 *
 * Regla de negocio:
 *   1. Una comisión se considera "pagada" cuando Entretur la registra en
 *      el sistema (liquidación con método real, no anticipo).
 *   2. El corte es POR AGENCIA dentro de cada fecha de salida.
 *   3. Filtro opcional por agencia.
 *
 * Modo:
 *   · vencimiento — todas las comisiones pendientes agrupadas por fecha
 *                   futura de salida (default).
 *   · origen      — solo las comisiones generadas por reservas creadas un
 *                   día específico (para responder "¿qué comisión generó
 *                   la venta del 16-ago y cuándo se paga?").
 */
import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box, Typography, Card, CardContent, TextField, MenuItem, Chip, Stack, Tooltip,
  ToggleButton, ToggleButtonGroup, CircularProgress, IconButton,
  Collapse, Alert, Switch, FormControlLabel,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ReservaFinancieroModal from './ReservaFinancieroModal';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}
function getEmpresaId() {
  try { return String(JSON.parse(localStorage.getItem('user') || '{}').company_id || '1'); }
  catch { return '1'; }
}

const fmtMoney = (n) => new Intl.NumberFormat('es-CO', {
  style: 'currency', currency: 'COP', maximumFractionDigits: 0,
}).format(Math.round(Number(n) || 0));

const fmtFecha = (iso) => {
  if (!iso) return '—';
  try {
    const [y, m, d] = iso.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    return dt.toLocaleDateString('es-CO', {
      weekday: 'short', day: '2-digit', month: 'short',
    });
  } catch { return iso; }
};

// Días desde hoy → chip visual (Hoy, En 3d, En 12d, etc.)
const diasDesdeHoy = (iso) => {
  if (!iso) return null;
  try {
    const [y, m, d] = iso.split('-').map(Number);
    const target = new Date(y, m - 1, d);
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const diff = Math.round((target - hoy) / (1000 * 60 * 60 * 24));
    if (diff === 0) return { label: 'HOY', color: '#dc2626', bg: '#fee2e2' };
    if (diff === 1) return { label: 'Mañana', color: '#ea580c', bg: '#ffedd5' };
    // Salida ya pasada — si aparece en el cronograma es porque tiene comisión
    // pendiente de pagar (liquidacion_id=NULL). Marcamos ROJO para alertar
    // que es un ATRASO, no un vencimiento futuro.
    if (diff < 0) return { label: `Atrasado ${-diff}d`, color: '#b91c1c', bg: '#fee2e2' };
    if (diff <= 7) return { label: `En ${diff}d`, color: '#0891b2', bg: '#cffafe' };
    return { label: `En ${diff}d`, color: '#475569', bg: '#f1f5f9' };
  } catch { return null; }
};

// Helpers de mes: manejamos YYYY-MM como identidad. `hoy` para el default;
// `sig`/`ant` navegan mes anterior/siguiente sin depender de Date.getMonth (que
// da problemas con timezone al construir strings). Trabajamos con enteros
// year/month puros y solo formateamos al render.
function _mesActual() {
  const d = new Date();
  return { y: d.getFullYear(), m: d.getMonth() + 1 }; // m: 1..12
}
function _mesShift({ y, m }, delta) {
  const total = y * 12 + (m - 1) + delta;
  return { y: Math.floor(total / 12), m: (total % 12) + 1 };
}
function _mesLabel({ y, m }) {
  const nombres = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
  return `${nombres[m - 1]} ${y}`;
}
function _mesRango({ y, m }) {
  // Devuelve {desde, hasta} en YYYY-MM-DD.
  //
  // Historia de esta función, porque va y viene: en algún momento se acotó a
  // hoy, después se abrió al mes completo para no esconder las comisiones ya
  // vencidas y sin pagar, y ahora vuelve a acotarse a hoy — esto es una
  // AGENDA de pagos, y una agenda planea plata que va a salir, no audita la
  // que debió salir. El mes en curso llegaba con 19 fechas atrasadas y 3
  // futuras, así que la pantalla llamada "futuras" mostraba sobre todo pasado.
  //
  // Lo vencido no se muestra en esta pantalla — es una decisión explícita,
  // no un olvido. Perseguir atrasos es otra tarea y va por otro lado.
  const primer = `${y}-${String(m).padStart(2,'0')}-01`;
  const ultimoDia = new Date(y, m, 0).getDate(); // día 0 del siguiente = último del actual
  const ultimo = `${y}-${String(m).padStart(2,'0')}-${String(ultimoDia).padStart(2,'0')}`;
  const hoy = _hoyIso();
  return { desde: primer < hoy ? hoy : primer, hasta: ultimo };
}

// Hoy en YYYY-MM-DD, con calendario local (Bogotá en el navegador del operador).
function _hoyIso() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}


export default function CronogramaComisiones() {
  const [modo, setModo] = useState('vencimiento'); // vencimiento | origen
  const [fechaVenta, setFechaVenta] = useState(new Date().toISOString().slice(0, 10));
  const [mesSel, setMesSel] = useState(_mesActual());       // {y, m}
  // Horizonte del modo 'vencimiento'. Por defecto 'futuro': todo lo que
  // queda por girar de hoy en adelante, sin tope de mes.
  //
  // Antes sólo existía el recorrido mes a mes, y eso escondía plata: las
  // comisiones dispersas en meses lejanos (26-sep, 24-oct) sólo aparecían
  // si uno adivinaba el mes al que había que moverse. Además el mes actual
  // se llena de fechas ATRASADAS, así que la pantalla llamada "futuras"
  // mostraba sobre todo pasado.
  const [horizonte, setHorizonte] = useState('futuro');   // futuro | mes
  const [verSinPendiente, setVerSinPendiente] = useState(false);
  const [fechaExpandida, setFechaExpandida] = useState(null);
  const [agenciaExpandida, setAgenciaExpandida] = useState(null); // "fecha|agencia_id"
  const [reservaModalId, setReservaModalId] = useState(null);

  // Rango del mes seleccionado (solo aplica en modo=vencimiento).
  const rangoMes = useMemo(() => _mesRango(mesSel), [mesSel]);

  // Cronograma
  const { data, isLoading, error } = useQuery({
    queryKey: ['cronograma-comisiones', modo, fechaVenta, horizonte,
               mesSel.y, mesSel.m, verSinPendiente, getEmpresaId()],
    queryFn: async () => {
      const params = new URLSearchParams({ empresa_id: getEmpresaId(), modo });
      if (modo === 'origen' && fechaVenta) params.set('fecha_venta', fechaVenta);
      // Sin fecha_desde/fecha_hasta el endpoint ya devuelve de hoy en
      // adelante, que es exactamente el horizonte 'futuro'.
      if (modo === 'vencimiento' && horizonte === 'mes') {
        params.set('fecha_desde', rangoMes.desde);
        params.set('fecha_hasta', rangoMes.hasta);
      }
      if (verSinPendiente) params.set('incluir_sin_pendiente', 'true');
      const r = await fetch(`${API_BASE}/turismo/comisiones/cronograma?${params}`,
        { headers: getAuthHeaders() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
  });

  const cronograma = data?.cronograma || [];
  const totalGeneral = data?.total_general || 0;

  // KPIs derivados. Los tres se calculan sobre lo que trajo el backend
  // (que ya vino acotado al mes seleccionado). El KPI de "próximos 7 días"
  // solo tiene sentido si el mes seleccionado incluye hoy — cuando ves un
  // mes futuro completo, no hay "7 días" que separar del resto.
  const kpis = useMemo(() => {
    const hoyISO = new Date().toISOString().slice(0, 10);
    const en7 = new Date();
    en7.setDate(en7.getDate() + 7);
    const en7ISO = en7.toISOString().slice(0, 10);
    let semana = 0;
    let mesTotal = 0;
    let numReservasMes = 0;
    for (const f of cronograma) {
      if (f.fecha_salida >= hoyISO && f.fecha_salida <= en7ISO) semana += f.comision_por_pagar;
      mesTotal += f.comision_por_pagar;
      numReservasMes += (f.num_reservas || 0);
    }
    return { semana, mesTotal, numReservasMes };
  }, [cronograma]);

  const mesEsActual = mesSel.y === _mesActual().y && mesSel.m === _mesActual().m;

  const toggleFecha = (fecha) => {
    setFechaExpandida(fechaExpandida === fecha ? null : fecha);
    setAgenciaExpandida(null); // colapsar sub-nivel al cambiar de fecha
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f8fafc', minHeight: '100%' }}>
      {/* Header — mismo patrón compacto que VentasDiaPage */}
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 800, color: '#0f172a', fontSize: 18 }}>
          Agenda de pagos a agencias
        </Typography>
        <Typography sx={{ fontSize: 12, color: '#64748b' }}>
          Cuándo sale plata y a quién · comisiones pendientes de hoy en adelante, por fecha de salida.
        </Typography>
      </Box>

      {/* Toggle de modo + filtros */}
      <Card sx={{ p: 2, mb: 2, borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} flexWrap="wrap">
          <ToggleButtonGroup
            value={modo}
            exclusive
            size="small"
            onChange={(_, v) => v && setModo(v)}
            sx={{
              '& .MuiToggleButton-root': {
                textTransform: 'none', fontSize: 12, px: 2, fontWeight: 600,
                '&.Mui-selected': { bgcolor: '#0d9488', color: '#fff', '&:hover': { bgcolor: '#0f766e' } },
              },
            }}
          >
            <ToggleButton value="vencimiento">Por vencimiento (futuras)</ToggleButton>
            <ToggleButton value="origen">Por origen (venta de un día)</ToggleButton>
          </ToggleButtonGroup>

          {modo === 'origen' && (
            <TextField
              type="date"
              size="small"
              label="Fecha de venta"
              value={fechaVenta}
              onChange={(e) => setFechaVenta(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 180 }}
            />
          )}

          {/* Selector de mes — solo aplica en modo=vencimiento. Navegación
              ‹ Sep 2026 ›, no permite retroceder antes del mes actual
              (proyección = futuro, no pasado). */}
          {modo === 'vencimiento' && (
            <Box sx={{ display: 'flex', gap: 0.4, bgcolor: '#e2e8f0', p: 0.4,
                        borderRadius: 2, flexShrink: 0 }}>
              {[
                { k: 'futuro', label: 'Todo lo pendiente' },
                { k: 'mes',    label: 'Por mes' },
              ].map(o => (
                <Box key={o.k} onClick={() => setHorizonte(o.k)} role="button" tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setHorizonte(o.k); }}
                  sx={{ px: 1.25, py: 0.5, borderRadius: 1.5, cursor: 'pointer',
                         fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
                         bgcolor: horizonte === o.k ? '#fff' : 'transparent',
                         color:   horizonte === o.k ? '#0f172a' : '#64748b',
                         boxShadow: horizonte === o.k ? '0 1px 3px rgba(15,23,42,.14)' : 'none',
                         transition: 'all .15s' }}>
                  {o.label}
                </Box>
              ))}
            </Box>
          )}

          {modo === 'vencimiento' && horizonte === 'mes' && (
            <Stack direction="row" spacing={0.5} alignItems="center"
              sx={{ bgcolor: '#f1f5f9', borderRadius: 2, px: 0.5, py: 0.25 }}>
              <IconButton size="small"
                disabled={mesEsActual}
                onClick={() => setMesSel(_mesShift(mesSel, -1))}
                sx={{ color: mesEsActual ? '#cbd5e1' : '#0d9488' }}>
                <ChevronLeftIcon fontSize="small" />
              </IconButton>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0f172a',
                                minWidth: 90, textAlign: 'center', textTransform: 'capitalize' }}>
                {_mesLabel(mesSel)}
              </Typography>
              <IconButton size="small"
                onClick={() => setMesSel(_mesShift(mesSel, 1))}
                sx={{ color: '#0d9488' }}>
                <ChevronRightIcon fontSize="small" />
              </IconButton>
              {!mesEsActual && (
                <Chip size="small" label="Hoy" onClick={() => setMesSel(_mesActual())}
                  sx={{ ml: 0.5, fontSize: 10, height: 20, cursor: 'pointer',
                        bgcolor: '#0d9488', color: '#fff',
                        '&:hover': { bgcolor: '#0f766e' } }} />
              )}
            </Stack>
          )}

          {/* Toggle "mostrar también sin pendiente" — clave para no
              confundir un mes con todo anticipado (pendiente=$0) con
              un mes vacío. */}
          {modo === 'vencimiento' && (
            <FormControlLabel
              control={
                <Switch size="small" checked={verSinPendiente}
                  onChange={(e) => setVerSinPendiente(e.target.checked)} />
              }
              label={
                <Typography sx={{ fontSize: 12, color: '#475569' }}>
                  Mostrar salidas ya cubiertas
                </Typography>
              }
              sx={{ ml: 0 }}
            />
          )}

        </Stack>
      </Card>

      {/* KPIs compactos — mismo patrón Tile que VentasDiaPage */}
      <Card sx={{ mb: 1.5, borderRadius: 2 }}>
        <Box sx={{ p: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography sx={{ fontSize: 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Resumen · {data?.num_fechas || 0} {data?.num_fechas === 1 ? 'fecha' : 'fechas'}
            </Typography>
          </Box>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 1,
          }}>
            <Tile
              label={horizonte === 'futuro' ? 'Total pendiente' : `Total ${_mesLabel(mesSel)}`}
              v={kpis.mesTotal}
              color="#0d9488" bg="#ccfbf1"
              hint={`${data?.num_fechas || 0} ${data?.num_fechas === 1 ? 'fecha' : 'fechas'} · ${kpis.numReservasMes} reservas`}
            />
            <Tile
              label={(horizonte === 'futuro' || mesEsActual) ? 'Próximos 7 días' : 'Este mes vs. hoy'}
              v={(horizonte === 'futuro' || mesEsActual) ? kpis.semana : 0}
              color="#dc2626" bg="#fee2e2"
              hint={(horizonte === 'futuro' || mesEsActual)
                ? 'Comisión a girar esta semana'
                : `Solo aplica al mes en curso — hoy estás viendo ${_mesLabel(mesSel)}`}
            />
            <Tile
              label="Reservas"
              v={kpis.numReservasMes}
              color="#7c3aed" bg="#ede9fe"
              raw
              hint={`Reservas del mes ${verSinPendiente ? '(incluye cubiertas)' : 'con comisión pendiente'}`}
            />
          </Box>
        </Box>
      </Card>

      {/* Cronograma */}
      {isLoading && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress size={32} sx={{ color: '#0d9488' }} />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error al cargar el cronograma: {String(error.message || error)}
        </Alert>
      )}

      {!isLoading && !error && cronograma.length === 0 && (
        <Card sx={{ p: 5, textAlign: 'center', borderRadius: 3 }}>
          <Typography sx={{ fontSize: 14, color: '#64748b', mb: 1 }}>
            {modo === 'origen'
              ? `Sin comisiones pendientes generadas por ventas del ${fmtFecha(fechaVenta)}.`
              : (horizonte === 'futuro'
                  ? 'Sin comisiones por pagar de hoy en adelante.'
                  : `Sin comisiones por pagar en ${_mesLabel(mesSel)}.`)}
          </Typography>
          {modo === 'vencimiento' && !verSinPendiente && (
            <Typography sx={{ fontSize: 12, color: '#94a3b8' }}>
              Puede haber salidas cuya comisión ya se anticipó al 100%.
              Activa <b>"Mostrar salidas ya cubiertas"</b> para verlas.
            </Typography>
          )}
        </Card>
      )}

      {!isLoading && cronograma.length > 0 && (
        <Card sx={{ mb: 2, borderRadius: 2 }}>
          <CardContent sx={{ p: 2 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0f172a', mb: 1 }}>
              Desembolsos por fecha de salida
            </Typography>

            <TableContainer component={Paper}
              sx={{ boxShadow: 'none', border: '1px solid #e2e8f0', overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 640 }}>
                <TableHead sx={{ bgcolor: '#f8fafc' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: 11 }}>Fecha de salida</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: 11,
                                                    display: { xs: 'none', sm: 'table-cell' } }}>Cuándo</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: 11 }}>Agencias</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: 11,
                                                    display: { xs: 'none', sm: 'table-cell' } }}>Reservas</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: 11 }}>Por pagar</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cronograma.map(f => {
                    const dias = diasDesdeHoy(f.fecha_salida);
                    const abierta = fechaExpandida === f.fecha_salida;
                    return (
                      <React.Fragment key={f.fecha_salida}>
                        <TableRow hover onClick={() => toggleFecha(f.fecha_salida)}
                          sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f0fdfa' } }}>
                          <TableCell sx={{ fontSize: 12, fontWeight: 600, color: '#0f172a' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <ExpandMoreIcon sx={{ fontSize: 16, color: '#0d9488',
                                transition: 'transform 0.2s',
                                transform: abierta ? 'rotate(180deg)' : 'none' }} />
                              <span style={{ textTransform: 'capitalize' }}>{fmtFecha(f.fecha_salida)}</span>
                            </Box>
                          </TableCell>
                          <TableCell align="center" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                            {dias && (
                              <Chip size="small" label={dias.label}
                                sx={{ bgcolor: dias.bg, color: dias.color, fontWeight: 700, fontSize: 10, height: 20 }} />
                            )}
                          </TableCell>
                          <TableCell align="center" sx={{ fontSize: 12 }}>{f.agencias.length}</TableCell>
                          <TableCell align="center" sx={{ fontSize: 12,
                                                          display: { xs: 'none', sm: 'table-cell' } }}>{f.num_reservas}</TableCell>
                          <TableCell align="right" sx={{ fontSize: 13, fontFamily: 'monospace',
                                                          fontWeight: 700, color: '#0d9488' }}>
                            {fmtMoney(f.comision_por_pagar)}
                          </TableCell>
                        </TableRow>
                        {/* Fila expandible: sub-tabla por agencia */}
                        <TableRow>
                          <TableCell colSpan={5} sx={{ p: 0, border: 0 }}>
                            <Collapse in={abierta} timeout="auto" unmountOnExit>
                              <Box sx={{ p: 1.5, bgcolor: '#f0fdfa',
                                          borderBottom: '1px solid #99f6e4' }}>
                                <SubTablaAgencias
                                  fecha={f.fecha_salida}
                                  agencias={f.agencias}
                                  agenciaExpandida={agenciaExpandida}
                                  setAgenciaExpandida={setAgenciaExpandida}
                                  onOpenReserva={setReservaModalId}
                                />
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Total footer — mismo patrón visual que la tabla, no card separado */}
            <Box sx={{
              mt: 1.5, p: 1.25, borderRadius: 1.5,
              bgcolor: '#0d9488', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.5 }}>
                TOTAL POR PAGAR
              </Typography>
              <Typography sx={{ fontSize: 15, fontWeight: 800, fontFamily: 'monospace' }}>
                {fmtMoney(totalGeneral)}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Modal de detalle de reserva (mismo que usan las otras pantallas de finanzas) */}
      {reservaModalId && (
        <ReservaFinancieroModal
          reservaId={reservaModalId}
          onClose={() => setReservaModalId(null)}
        />
      )}
    </Box>
  );
}

// ---------- Sub-tabla de agencias dentro del acordeón de fecha ----------
// Mismo patrón que la sub-fila expandible de VentasDiaPage: tabla anidada
// con header más suave y filas expandibles para ver las reservas.
function SubTablaAgencias({ fecha, agencias, agenciaExpandida, setAgenciaExpandida, onOpenReserva }) {
  return (
    <TableContainer component={Paper}
      sx={{ boxShadow: 'none', border: '1px solid #ccfbf1',
            overflowX: 'auto', bgcolor: '#fff' }}>
      <Table size="small" sx={{ minWidth: 520 }}>
        <TableHead sx={{ bgcolor: '#ecfdf5' }}>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, fontSize: 10.5, color: '#0f766e' }}>Agencia</TableCell>
            <TableCell align="center" sx={{ fontWeight: 700, fontSize: 10.5, color: '#0f766e' }}>Reservas</TableCell>
            <TableCell align="right" sx={{ fontWeight: 700, fontSize: 10.5, color: '#0f766e' }}>Por pagar</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {agencias.map(a => {
            const key = `${fecha}|${a.agencia_id}`;
            const abierta = agenciaExpandida === key;
            return (
              <React.Fragment key={key}>
                <TableRow hover
                  onClick={() => setAgenciaExpandida(abierta ? null : key)}
                  sx={{ cursor: 'pointer', '&:hover': { bgcolor: '#f0fdfa' } }}>
                  <TableCell sx={{ fontSize: 12, fontWeight: 600, color: '#0f172a' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ExpandMoreIcon sx={{ fontSize: 15, color: '#be185d',
                        transition: 'transform 0.2s',
                        transform: abierta ? 'rotate(180deg)' : 'none' }} />
                      {a.agencia_nombre}
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{ fontSize: 12 }}>{a.num_reservas}</TableCell>
                  <TableCell align="right" sx={{ fontSize: 12, fontFamily: 'monospace',
                                                  fontWeight: 700, color: '#be185d' }}>
                    {fmtMoney(a.comision_por_pagar)}
                  </TableCell>
                </TableRow>
                {/* Reservas de la agencia — mismo patrón que la sub-fila de Ventas */}
                <TableRow>
                  <TableCell colSpan={3} sx={{ p: 0, border: 0 }}>
                    <Collapse in={abierta} timeout="auto" unmountOnExit>
                      <Box sx={{ p: 1, bgcolor: '#fdf2f8',
                                  borderBottom: '1px solid #fbcfe8' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {a.reservas.map(r => (
                            <Box key={r.id}
                              onClick={() => onOpenReserva(r.id)}
                              sx={{
                                display: 'flex', gap: 1, alignItems: 'center',
                                p: 0.75, borderRadius: 1, bgcolor: '#fff',
                                border: '1px solid #e2e8f0', flexWrap: 'wrap',
                                cursor: 'pointer',
                                '&:hover': { borderColor: '#0d9488',
                                             boxShadow: '0 0 0 1px #0d9488' },
                              }}>
                              <Typography sx={{ fontSize: 11, fontFamily: 'monospace',
                                                fontWeight: 700, color: '#0f172a' }}>
                                {r.codigo_reserva}
                              </Typography>
                              <Typography sx={{ fontSize: 11, color: '#334155', flex: 1 }}>
                                {r.nombre_cliente || '—'}
                              </Typography>
                              {r.comision_anticipada > 0 && (
                                <Chip size="small" label={`Antic. ${fmtMoney(r.comision_anticipada)}`}
                                  sx={{ bgcolor: '#fef3c7', color: '#92400e',
                                        fontSize: 9, height: 18, fontFamily: 'monospace' }} />
                              )}
                              {r.abonado > 0 && (
                                <Chip size="small" label={`Abn. ${fmtMoney(r.abonado)}`}
                                  sx={{ bgcolor: '#dbeafe', color: '#1d4ed8',
                                        fontSize: 9, height: 18, fontFamily: 'monospace' }} />
                              )}
                              <Typography sx={{ fontSize: 11, fontFamily: 'monospace',
                                                fontWeight: 700, color: '#be185d' }}>
                                {fmtMoney(r.comision_por_pagar)}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ---------- Tile compacto — mismo patrón que VentasDiaPage ----------
// Cuadrito con fondo tenue, label chico arriba en uppercase, valor grande
// abajo en formato COMPLETO colombiano ($6.133.000 con puntos de miles),
// no compacto — el usuario prefiere leer la cifra exacta de un vistazo.
function Tile({ label, v, color, bg, hint, raw }) {
  // `raw` desactiva el prefijo `$` — para tiles que muestran conteos
  // (ej. número de reservas) en vez de dinero.
  const valor = Number(v || 0);
  const numero = new Intl.NumberFormat('es-CO', { maximumFractionDigits: 0 }).format(valor);
  const textoExacto = raw ? numero : `$${numero}`;

  const contenido = (
    <Box sx={{
      p: { xs: 1, md: 1.25 }, bgcolor: bg, borderRadius: 1.5,
      cursor: hint ? 'help' : 'default',
      height: '100%',
      display: 'flex', flexDirection: 'column',
      justifyContent: 'space-between',
      minWidth: 0, overflow: 'hidden',
      gap: 0.5,
    }}>
      <Typography sx={{
        fontSize: { xs: 9, sm: 9, lg: 8.5 },
        color, fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: 0.3, lineHeight: 1.2,
        borderBottom: hint ? '1px dotted currentColor' : 'none',
        alignSelf: 'flex-start',
        display: '-webkit-box', WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical', overflow: 'hidden',
        wordBreak: 'break-word',
      }}>
        {label}
      </Typography>
      <Typography sx={{
        // Fontsize levemente más chico que la versión compacta para que
        // `$99.999.999` (11 chars) quepa cómodo en el ancho del tile.
        fontSize: { xs: 14, sm: 15, md: 14, lg: 13, xl: 15 },
        fontWeight: 800, color,
        fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace',
        lineHeight: 1.2,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        fontVariantNumeric: 'tabular-nums',
      }}>
        {textoExacto}
      </Typography>
    </Box>
  );

  return hint
    ? <Tooltip title={hint} arrow>{contenido}</Tooltip>
    : contenido;
}
